import { Fragment, useContext, useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

import { PIE_CHART_OTHERS_COLOR } from './constants'
import { PieChartContext } from './pie-chart-context'
import { AnimatedPieSlice, makeSlicePath, makeHaloPath } from './pie-slice'
import type { PieChartSliceData } from './types'

/**
 * Angle offset to align the first slice with the top of the chart.
 */
const ANGLE_OFFSET = -Math.PI / 2

interface Props {
  data: PieChartSliceData[]
  /**
   * X-coordinate of the center of the pie chart.
   * E.g. '50%', 100, ...
   */
  cx?: number | string
  /**
   * Y-coordinate of the center of the pie chart.
   * E.g. '50%', 100, ...
   */
  cy?: number | string
  /**
   * Inner radius of the pie chart.
   */
  innerRadius?: number
  /**
   * Outer radius of the pie chart.
   */
  outerRadius: number
  /**
   * Custom color of the hole in the pie chart. Default is white.
   */
  holeColor?: string
}

export const PieChartPie: React.FC<Props> = ({
  data,
  cx = '50%',
  cy = '50%',
  innerRadius = 0,
  outerRadius,
  holeColor = 'white',
}: Props) => {
  const containerRef = useRef<SVGGElement>(null)
  const ctx = useContext(PieChartContext)

  if (!ctx) {
    throw new Error('<PieChartPie> must be inside a <PieChart>')
  }

  const cx_ = resolveCenterCoordinate(cx, ctx.width)
  const cy_ = resolveCenterCoordinate(cy, ctx.height)
  const total = data.reduce((acc, { value }) => acc + value, 0)

  // Close the tooltip when clicking outside the pie.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        ctx.setHovered(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [ctx])

  const hovered = ctx.hovered ?? ctx.tooltipHovered ?? null

  /**
   * Accumulated value of the slices as we iterate over them.
   */
  let accum = 0

  return (
    <g ref={containerRef}>
      {/* BACKGROUND CIRCLE */}
      <circle cx={cx_} cy={cy_} r={outerRadius} fill="#f1f3f5" />

      {data.map((d, i) => {
        const isHovered = hovered?.index === i
        const isAnyHovered = hovered != null

        const startAngle = (accum / total) * (2 * Math.PI) + ANGLE_OFFSET
        const endAngle = ((accum + d.value) / total) * (2 * Math.PI) + ANGLE_OFFSET
        accum += d.value

        const midAngle = (startAngle + endAngle) / 2
        const midRadius = innerRadius + (outerRadius - innerRadius) / 2
        const midX = cx_ + Math.cos(midAngle) * midRadius
        const midY = cy_ + Math.sin(midAngle) * midRadius

        const color = d.others ? PIE_CHART_OTHERS_COLOR : ctx.colors[i % ctx.colors.length]

        const haloRadius = outerRadius + 8
        const pathHalo = makeHaloPath(cx_, cy_, haloRadius, startAngle, endAngle)
        const path = makeSlicePath(cx_, cy_, outerRadius, startAngle, endAngle)

        return (
          <Fragment key={i}>
            {/* HALO */}
            <path
              style={{
                transitionTimingFunction: 'cubic-bezier(0,0,0.35,1)',
                transitionProperty: 'opacity',
                transitionDuration: '80ms',
              }}
              d={pathHalo}
              fill={color}
              opacity={isHovered ? 0.35 : 0}
            />

            {/* MAIN SLICE */}
            <AnimatedPieSlice
              className={cn({
                'cursor-pointer': !hovered?.locked,
              })}
              cx={cx_}
              cy={cy_}
              radius={outerRadius}
              startAngle={startAngle}
              endAngle={endAngle}
              fill={color}
              onMouseEnter={() => {
                ctx.setHovered((prev) => {
                  if (!prev) {
                    return { index: i, x: midX, y: midY, locked: false }
                  }
                  if (prev.locked) {
                    return prev
                  }
                  return { index: i, x: midX, y: midY, locked: false }
                })
              }}
              onClick={() => {
                ctx.setHovered((prev) => {
                  if (!prev) {
                    return { index: i, x: midX, y: midY, locked: true }
                  }
                  if (prev.locked) {
                    return { index: i, x: midX, y: midY, locked: false }
                  }
                  return { index: i, x: midX, y: midY, locked: true }
                })
              }}
              onMouseLeave={() => {
                ctx.setHovered((prev) => {
                  if (!prev) return null
                  if (prev.locked) return prev
                  return null
                })
              }}
            />

            {/* BACKDROP FOR HOVER EFFECT */}
            <path
              style={{
                transitionTimingFunction: 'cubic-bezier(0,0,0.35,1)',
                transitionProperty: 'opacity',
                transitionDuration: '80ms',
                pointerEvents: 'none',
                opacity: isAnyHovered && !isHovered ? 0.5 : 0,
              }}
              d={path}
              fill={'black'}
            />
          </Fragment>
        )
      })}

      {/* INNER CIRCLE */}
      {innerRadius > 0 && (
        <g>
          <circle cx={cx_} cy={cy_} r={innerRadius} fill={holeColor} className="pointer-events-none" />
          <text
            x={cx_}
            y={cy_}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-[#1e1e1e] text-[30px] font-[600] tracking-[-0.225px]"
          >
            {total}
          </text>
        </g>
      )}
    </g>
  )
}
PieChartPie.displayName = 'PieChartPie'

/**
 * Resolves percentage values to numbers or pass through px numbers.
 */
function resolveCenterCoordinate(val: number | string, full: number) {
  if (typeof val === 'string' && val.endsWith('%')) {
    return (parseFloat(val) / 100) * full
  }
  return Number(val)
}
